import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Send, 
  X, 
  Sparkles,
  Loader2,
  Bot,
  Minimize2,
  Maximize2,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AIAssistantWidget = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  // Show welcome message when first opened
  useEffect(() => {
    if (isOpen && conversation.length === 0) {
      const welcomeMessage = {
        role: 'assistant',
        content: `Hi ${user?.name?.split(' ')[0] || 'there'}! I'm your Vision Lab AI Assistant. I'm here to help you with anything related to your brand journey.\n\nYou can ask me about:\n• Your brand strategy and positioning\n• Content ideas for your audience\n• How to use any of our tools\n• Feedback on your brand progress\n\nWhat would you like to work on today?`
      };
      setConversation([welcomeMessage]);
    }
  }, [isOpen, user?.name]);

  const quickPrompts = [
    "What should I focus on next?",
    "Give me content ideas",
    "Review my brand progress",
    "Help me with my positioning"
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!userMessage.trim() || loading) return;

    const newUserMessage = { role: 'user', content: userMessage };
    setConversation(prev => [...prev, newUserMessage]);
    setLoading(true);
    setUserMessage('');

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/ai/assistant`, {
        message: userMessage,
        conversation_history: conversation
      }, { headers });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response
      };
      
      setConversation(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error('Failed to get response');
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setUserMessage(prompt);
  };

  const openFullPage = () => {
    setIsOpen(false);
    navigate('/dashboard/ai-assistant');
  };

  // Don't show widget if not logged in
  if (!token) return null;

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => {
              setIsOpen(true);
              setHasNewMessage(false);
            }}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#E5C585] rounded-full flex items-center justify-center shadow-lg hover:shadow-[#D4AF37]/20 transition-shadow group"
            data-testid="ai-assistant-trigger"
          >
            <Bot size={24} className="text-black" />
            {hasNewMessage && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full" />
            )}
            <div className="absolute right-full mr-3 px-3 py-1.5 bg-[#1C1C1C] text-white text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Vision Lab AI
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 'auto' : '500px'
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] bg-[#0A0A0A] border border-[#1C1C1C] shadow-2xl flex flex-col overflow-hidden"
            data-testid="ai-assistant-widget"
          >
            {/* Header */}
            <div className="p-4 border-b border-[#1C1C1C] flex items-center justify-between bg-gradient-to-r from-[#0A0A0A] to-[#1C1C1C]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37]/20 to-[#E5C585]/10 flex items-center justify-center">
                  <Bot className="text-[#D4AF37]" size={20} />
                </div>
                <div>
                  <h3 className="font-serif text-white text-sm">Vision Lab AI</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-[#52525B] text-xs">Online</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={openFullPage}
                  className="text-[#52525B] hover:text-[#D4AF37] transition-colors p-2"
                  title="Open full page"
                >
                  <ExternalLink size={16} />
                </button>
                <button 
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="text-[#52525B] hover:text-white transition-colors p-2"
                >
                  {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-[#52525B] hover:text-white transition-colors p-2"
                  data-testid="close-assistant-widget"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Chat Content */}
            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '320px' }}>
                  {conversation.map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] p-3 ${
                        msg.role === 'user' 
                          ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/20' 
                          : 'bg-[#1C1C1C]'
                      }`}>
                        {msg.role === 'assistant' && (
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles size={12} className="text-[#D4AF37]" />
                            <span className="text-[#D4AF37] text-[10px] font-accent tracking-wider">VISION LAB AI</span>
                          </div>
                        )}
                        <p className="text-[#E4E4E7] text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      </div>
                    </motion.div>
                  ))}

                  {/* Loading */}
                  {loading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-[#1C1C1C] p-3 flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin text-[#D4AF37]" />
                        <span className="text-[#A1A1AA] text-sm">Thinking...</span>
                      </div>
                    </motion.div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Quick prompts */}
                {conversation.length <= 1 && (
                  <div className="px-4 pb-2">
                    <div className="flex flex-wrap gap-1.5">
                      {quickPrompts.map((prompt, i) => (
                        <button
                          key={i}
                          onClick={() => handleQuickPrompt(prompt)}
                          className="text-xs px-2.5 py-1 border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input */}
                <div className="p-3 border-t border-[#1C1C1C]">
                  <form onSubmit={handleSubmit} className="flex gap-2">
                    <Textarea
                      value={userMessage}
                      onChange={(e) => setUserMessage(e.target.value)}
                      placeholder="Ask me anything..."
                      className="flex-1 bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-2.5 min-h-[44px] max-h-[80px] text-white placeholder:text-[#52525B] text-sm resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit();
                        }
                      }}
                      data-testid="assistant-input"
                    />
                    <Button
                      type="submit"
                      disabled={loading || !userMessage.trim()}
                      className="btn-gold px-3 self-end h-[44px]"
                      data-testid="assistant-submit"
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </Button>
                  </form>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistantWidget;
